import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

import { loginRequest } from './auth-config';
import { CiamAuthService, SUPPORT_RAIL_KEY } from './ciam-auth.service';
import { SupportPrincipalService } from './support-principal.service';
import { FooterComponent } from '../shell/footer.component';

/**
 * Sign-in landing (§Support Console CIAM login design). Two rails:
 *  - BlueTitan staff → workforce tenant via the msal-angular MsalService instance.
 *  - Partner (VAR) → CIAM tenant via CiamAuthService's standalone instance.
 * Each rail stamps sessionStorage `bt_support_rail` BEFORE redirecting so the return
 * leg is processed by the matching instance (app.config initializer) and the interceptor
 * attaches the right token. Unguarded route — this IS the entry point authenticatedGuard
 * sends unauthenticated visitors to, so it must never require auth. The partner rail is
 * hidden where CIAM is unconfigured (prod, until the prod CIAM app reg exists).
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [RouterLink, NgbTooltip, FooterComponent],
  template: `
    <div class="login-bg">
      <div class="login-shell">
        <header class="login-brand">
          <img class="login-brand__logo login-brand__logo--light" src="assets/logos/Recon/Blue-Titan-Emblem-Logo-Recon@4x.png" alt="" />
          <img class="login-brand__logo login-brand__logo--dark" src="assets/logos/White/Blue-Titan-Emblem-Logo-White@4x.png" alt="" />
          <span class="bt-wordmark login-brand__name" data-testid="bt-wordmark">BlueTitan&nbsp;Support</span>
        </header>

        <div class="bt-card login-card">
          <div class="login-head">
            <h1 class="login-title">Sign in</h1>
            <p class="login-intro">Choose how you sign in.</p>
          </div>

          <div class="login-rails">
            <button type="button" class="bt-btn bt-btn--primary login-rail" data-testid="login-staff"
                    (click)="signInStaff()"
                    ngbTooltip="For BlueTitan employees — sign in with your BlueTitan work account.">
              BlueTitan staff
            </button>

            @if (partnerRailEnabled) {
              <button type="button" class="bt-btn bt-btn--secondary login-rail" data-testid="login-partner"
                      (click)="signInPartner()"
                      ngbTooltip="For certified partners (VARs) — sign in with your partner account.">
                Partner sign-in
              </button>
            }
          </div>

          <p class="login-help">
            Can't sign in?
            <a routerLink="/intake" data-testid="login-intake-link"
               ngbTooltip="Report a login problem — no account needed.">Report a login problem</a>
          </p>
        </div>
      </div>
      <!-- Legal line only: branding.md mandates the tagline on public /
           marketing-adjacent surfaces (e.g. /intake), and a sign-in page is
           neither. -->
      <app-footer [showTagline]="false"></app-footer>
    </div>
  `,
  styles: [`
    .login-bg {
      min-height: 100vh; display: flex; flex-direction: column; align-items: center;
      padding: 2.5rem 1.25rem 1.5rem;
      background:
        radial-gradient(1100px 480px at 50% -8%, color-mix(in srgb, var(--bt-brand-intercept) 16%, transparent), transparent 70%),
        var(--bt-bg-body);
    }
    .login-shell { width: 100%; max-width: 28rem; flex: 1; display: flex; flex-direction: column; justify-content: center; }
    .login-brand { display: flex; align-items: center; justify-content: center; gap: 0.6rem; margin-bottom: 1.5rem; }
    .login-brand__logo { height: 40px; width: auto; }
    .login-brand__logo--dark { display: none; }
    .login-brand__name { font-size: 1.5rem; font-weight: 700; color: var(--bt-text-primary); }

    .login-card { padding: 2rem 2.25rem; }
    .login-head { margin-bottom: 1.5rem; text-align: center; }
    .login-title { font-family: 'Montserrat', sans-serif; font-size: 1.6rem; font-weight: 700; margin: 0 0 0.4rem; color: var(--bt-text-primary); }
    .login-intro { margin: 0; color: var(--bt-text-secondary); }

    .login-rails { display: flex; flex-direction: column; gap: 0.85rem; }
    .login-rail { width: 100%; justify-content: center; padding: 0.7rem 1.4rem; font-size: 15px; }

    .login-help { margin: 1.4rem 0 0; text-align: center; font-size: 13px; color: var(--bt-text-secondary); }
    .login-help a { color: var(--bt-brand-intercept); }

    :host-context(body.theme-dark) .login-brand__logo--light { display: none; }
    :host-context(body.theme-dark) .login-brand__logo--dark { display: block; }

    @media (max-width: 480px) { .login-card { padding: 1.5rem 1.25rem; } }
  `],
})
export class LoginComponent implements OnInit {
  private msal = inject(MsalService);
  private ciam = inject(CiamAuthService);
  private principal = inject(SupportPrincipalService);
  private router = inject(Router);

  /** Partner rail is offered only where CIAM is configured (hidden in prod for now). */
  readonly partnerRailEnabled = this.ciam.isConfigured();

  ngOnInit(): void {
    // An already-authenticated visitor has no business on the login page — send them
    // home. /inbox is the canonical landing; the role/cert guards there route a VAR on
    // (uncertified → certification, certified → inbox), so this one target serves all.
    if (this.principal.isSignedIn()) {
      this.router.navigate(['/inbox']);
    }
  }

  signInStaff(): void {
    sessionStorage.setItem(SUPPORT_RAIL_KEY, 'staff');
    this.msal.loginRedirect(loginRequest);
  }

  signInPartner(): void {
    // CiamAuthService.loginRedirect stamps the rail 'partner' itself before redirecting.
    this.ciam.loginRedirect();
  }
}
